import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  LONG_MESSAGE_EXPIRY_MS,
} from "../constants/chat";
import { Message } from "../api/types";

type AddMessageParams = {
  text: string;
  sender: "sent" | "received";
  status?: "sent" | "delivered" | "failed";
  id?: string;
  initialMode?: "short" | "long";
  dateCreated?: string;
  expiresAt?: number;
  isPageActive: boolean;
};

type UseMessageExpiryReturn = {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  addMessage: (params: AddMessageParams) => string;
  shortenMessage: (messageId: string) => void;
  shortenAllMessages: (sender: "sent" | "received") => void;
};

export function useMessageExpiry(
  shortMessageExpiryMs: number,
): UseMessageExpiryReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesRef = useRef<Message[]>([]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const sortMessages = useCallback((items: Message[]) => {
    return [...items].sort((a, b) => {
      const aTime = Number(a.dateCreated || 0);
      const bTime = Number(b.dateCreated || 0);

      if (aTime !== bTime) return aTime - bTime;

      return a.id.localeCompare(b.id);
    });
  }, []);

  const addMessage = useCallback(
    ({
      text,
      sender,
      status = "sent",
      id,
      initialMode,
      dateCreated,
      expiresAt,
      isPageActive,
    }: AddMessageParams) => {
      const messageId = id || uuidv4();
      const createdAt = dateCreated || String(Date.now());

      let mode: "short" | "long";

      if (initialMode) {
        mode = initialMode;
      } else if (sender === "received") {
        mode = isPageActive ? "short" : "long";
      } else {
        mode = "long";
      }

      const messageExpiresAt =
        expiresAt ??
        Date.now() +
          (mode === "short" ? shortMessageExpiryMs : LONG_MESSAGE_EXPIRY_MS);

      const newMessage: Message = {
        id: messageId,
        text,
        sender,
        status,
        dateCreated: createdAt,
        expiresAt: messageExpiresAt,
        expireMode: mode,
      };

      setMessages((prev) => {
        const alreadyExists = prev.some((message) => message.id === messageId);
        if (alreadyExists) return prev;

        return sortMessages([...prev, newMessage]);
      });

      return messageId;
    },
    [shortMessageExpiryMs, sortMessages],
  );

  const shortenMessage = useCallback(
    (messageId: string) => {
      setMessages((prev) =>
        prev.map((message) => {
          if (message.id !== messageId) return message;

          /**
           * Important:
           * If the message already has a sooner expiry, keep it.
           * Do not extend it by resetting the timer from "now".
           */
          const newExpiresAt = Date.now() + shortMessageExpiryMs;
          const currentExpiresAt = message.expiresAt ?? Number.MAX_SAFE_INTEGER;

          return {
            ...message,
            expireMode: "short",
            expiresAt: Math.min(currentExpiresAt, newExpiresAt),
          };
        }),
      );
    },
    [shortMessageExpiryMs],
  );

  const shortenAllMessages = useCallback(
    (sender: "sent" | "received") => {
      setMessages((prev) =>
        prev.map((message) => {
          if (message.sender !== sender) return message;

          const newExpiresAt = Date.now() + shortMessageExpiryMs;
          const currentExpiresAt = message.expiresAt ?? Number.MAX_SAFE_INTEGER;

          return {
            ...message,
            expireMode: "short",
            expiresAt: Math.min(currentExpiresAt, newExpiresAt),
          };
        }),
      );
    },
    [shortMessageExpiryMs],
  );

  /**
   * One always-running expiry checker.
   * No per-message timeout.
   * No timer reset problem.
   */
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const now = Date.now();

      setMessages((prev) =>
        prev.filter((message) => {
          if (!message.expiresAt) return true;
          return message.expiresAt > now;
        }),
      );
    }, 250);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return {
    messages,
    setMessages,
    addMessage,
    shortenMessage,
    shortenAllMessages,
  };
}
